import React, { useEffect, useState } from 'react';
import { BsCalendar, BsX } from 'react-icons/bs';
import { Box, Icon, Input, InputGroup, InputRightElement, Popover, PopoverBody, PopoverContent, PopoverTrigger, Text, useDisclosure } from '@chakra-ui/react';
import { useDayzed } from 'dayzed';
import PropTypes from 'prop-types';

import colors from '../../styles/theme/foundation/colors';
import { getDateObject, getDateString } from '../../utils/dateUtil';

import Calendar from './Calendar';

const DatePickerCalendar = props => {
  const data = useDayzed(props);

  return <Calendar {...data} />;
};

const DatePicker = ({
  minDate,
  maxDate,
  dateFormat,
  width = '300px',
  placeholderText = 'Date',
  existingDate = '',
  isDisabled = false,
  textStyle = 'bodyMd',
  dataAnalyticsId = undefined,
  onDateSelect = () => {},
  error,
  ...rest
}) => {
  const [selectedDate, setDate] = useState(existingDate);
  const [proposedDate, setProposedDate] = useState(existingDate);
  const [dateError, setDateError] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    setDate(existingDate);
    setProposedDate(getDateString(existingDate, dateFormat));
  }, [existingDate]);

  useEffect(() => {
    if (error && error.message) {
      setDateError(error);
      setDate('');
      setProposedDate('');
    } else {
      setDateError(false);
    }
  }, [error]);

  const handleDateSelected = ({ selected, date }) => {
    if (dateError) setDateError(false);
    setDate(date);
    setProposedDate(getDateString(date, dateFormat));
    onDateSelect(date);
    onClose();
  };

  const resetDate = evt => {
    evt.stopPropagation();
    setDate('');
    setProposedDate('');
    setDateError(false);
    onDateSelect('');
  };

  const handleDateType = evt => {
    if (dateError) setDateError(false);
    setProposedDate(evt.target.value);
  };

  const isValid = dateObject => {
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(dateObject)) {
      setProposedDate('');
      setDateError(true);
      setDate('');
    } else {
      onDateSelect(dateObject);
      setDate(dateObject);
      setProposedDate(getDateString(dateObject, dateFormat));
    }
  };

  let boxShadowStyle;
  if (isOpen) {
    boxShadowStyle = `inset 0 0 0 1px ${colors.primaryScale['600']}`;
  } else if (dateError) {
    boxShadowStyle = `inset 0 0 0 1px ${colors.error.primary['600']}`;
  }

  let textColor;
  if (dateError) {
    textColor = colors.error.primary['600'];
  } else if (proposedDate) {
    textColor = colors.greyScale['900'];
  } else {
    textColor = colors.greyScale['500'];
  }

  return (
    <Box>
      <InputGroup size={rest.size} width={width} zIndex={isOpen ? 'popover' : 1}>
        <Input
          {...rest}
          boxShadow={boxShadowStyle}
          color={textColor}
          data-analytics-id={dataAnalyticsId}
          isDisabled={isDisabled}
          placeholder={placeholderText}
          textStyle={textStyle}
          value={proposedDate}
          variant="primary"
          onBlur={() => {
            if (proposedDate) {
              const dateObj = getDateObject(proposedDate);
              isValid(dateObj);
            }
          }}
          onChange={evt => handleDateType(evt)}
        />
        <Popover
          closeOnBlur={false}
          isOpen={isDisabled ? false : isOpen}
          onClose={onClose}
          onOpen={onOpen}
        >
          <PopoverTrigger>
            <InputRightElement
              _hover={{ cursor: 'pointer' }}
              children={(
                      selectedDate ? (
                        <Box>
                          <Icon as={BsCalendar} />
                          <Icon as={BsX} boxSize="18px" onClick={evt => resetDate(evt)} />
                        </Box>
                      ) : (
                        <Box>
                          <Icon as={BsCalendar} />
                        </Box>
                      )
                  )}
              color={selectedDate ? colors.greyScale['900'] : colors.greyScale['500']}
              right="9px"
              top="0px"
            />
          </PopoverTrigger>
          <PopoverContent border="none" borderRadius="xl" height="auto" width="368px">
            <PopoverBody p={4} zIndex={9999}>
              <DatePickerCalendar
                showOutsideDays
                maxDate={maxDate}
                minDate={minDate}
                selected={selectedDate}
                onDateSelected={handleDateSelected}
              />
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </InputGroup>
      <Box height="15px" mt="5px">
        {dateError && (
          <Text color={colors.error.primary['600']} textStyle="bodyXs500">
            {dateError.message ?? 'Invalid date'}
          </Text>
        )}
      </Box>
    </Box>

  );
};

/* Dayzed documentation: https://github.com/deseretdigital/dayzed */
DatePicker.propTypes = {
  /* Width of date picker input */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /* Text shown if no date is selected */
  placeholderText: PropTypes.string,
  /* Initial date before date is selected, i.e. from database */
  existingDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  /* Date format can be passed as MM/DD/YYY or will return as YYYY-MM-DD */
  dateFormat: PropTypes.string,
  /* True if input is disabled */
  isDisabled: PropTypes.bool,
  /* Text style for placeholder and selected date shown in input */
  textStyle: PropTypes.string,
  /* Callback function called when a new date is selected and handles the date change in the parent component */
  onDateSelect: PropTypes.func,
  /* Date object that restricts the user from selecting any dates prior to the provided date */
  // eslint-disable-next-line react/forbid-prop-types
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /* Date object that restricts the user from selecting any dates after the provided date */
  // eslint-disable-next-line react/forbid-prop-types
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /* Optional data attribute to track analytics */
  dataAnalyticsId: PropTypes.string,
  /* Error boolean or object passed from FormInput component */
  error: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({ message: PropTypes.string })])
};

export default DatePicker;
